import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Loader2, Download, Inbox, FileQuestion } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { AdminAPI } from '../../lib/api';
import { downloadCsv } from '../../lib/csv';

const fmt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const TabButton = ({ active, onClick, children, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`px-5 py-2.5 rounded-full text-sm font-bold btn-transition ${active ? 'bg-[#0A2A63] text-white shadow' : 'bg-white text-slate-600 border border-slate-200 hover:border-[#0A2A63]'}`}
  >
    {children}
  </button>
);

const AdminContacts = () => {
  const { toast } = useToast();
  const [tab, setTab] = useState('contacts');
  const [contacts, setContacts] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, q, p] = await Promise.all([
        AdminAPI.listContacts(),
        AdminAPI.listQuotes(),
        AdminAPI.listProducts().catch(() => []),
      ]);
      setContacts(c); setQuotes(q); setProducts(p);
    } catch { toast({ title: 'Error', description: 'Could not load contacts.' }); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const productName = useMemo(() => {
    const m = {};
    products.forEach((p) => { m[p.id] = p.name; });
    return (id) => m[id] || id || '—';
  }, [products]);

  const exportContacts = () => {
    downloadCsv('proofnation-contacts.csv', [
      { label: 'Date', value: (r) => fmt(r.created_at) },
      { label: 'Name', value: 'name' },
      { label: 'Email', value: 'email' },
      { label: 'Company', value: 'company' },
      { label: 'Phone', value: 'phone' },
      { label: 'Message', value: 'message' },
    ], contacts);
    toast({ title: 'Exported', description: `${contacts.length} contacts downloaded.` });
  };

  const exportQuotes = () => {
    downloadCsv('proofnation-quote-requests.csv', [
      { label: 'Date', value: (r) => fmt(r.created_at) },
      { label: 'Name', value: 'name' },
      { label: 'Email', value: 'email' },
      { label: 'Company', value: 'company' },
      { label: 'Phone', value: 'phone' },
      { label: 'Product', value: (r) => productName(r.product_id) },
      { label: 'Message', value: 'message' },
    ], quotes);
    toast({ title: 'Exported', description: `${quotes.length} quote requests downloaded.` });
  };

  const rows = tab === 'contacts' ? contacts : quotes;

  return (
    <div data-testid="admin-contacts-page">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-black text-[#050F2E] tracking-tight">Contacts</h1>
          <p className="text-slate-500 mt-1">People who left their contact details on the site</p>
        </div>
        <Button
          onClick={tab === 'contacts' ? exportContacts : exportQuotes}
          disabled={!rows.length}
          className="bg-[#FF4E50] hover:bg-[#FF7273] text-[#050F2E] rounded-full h-11 px-6 font-bold"
          data-testid="contacts-export"
        >
          <Download className="h-4 w-4 mr-2" /> Export CSV
        </Button>
      </div>

      <div className="flex items-center gap-3 mb-6">
        <TabButton active={tab === 'contacts'} onClick={() => setTab('contacts')} testId="tab-contacts">
          <span className="inline-flex items-center gap-2"><Inbox className="h-4 w-4" /> Contact form ({contacts.length})</span>
        </TabButton>
        <TabButton active={tab === 'quotes'} onClick={() => setTab('quotes')} testId="tab-quotes">
          <span className="inline-flex items-center gap-2"><FileQuestion className="h-4 w-4" /> Quote requests ({quotes.length})</span>
        </TabButton>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-16 text-center text-slate-400">
          No entries yet.
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-widest">
              <tr>
                <th className="text-left p-4">Date</th>
                <th className="text-left p-4">Name</th>
                <th className="text-left p-4">Email</th>
                <th className="text-left p-4">Company</th>
                {tab === 'quotes' && <th className="text-left p-4">Product</th>}
                <th className="text-left p-4">Message</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50 align-top" data-testid={`contact-row-${r.id}`}>
                  <td className="p-4 text-slate-500 whitespace-nowrap">{fmt(r.created_at)}</td>
                  <td className="p-4 font-bold text-[#050F2E] whitespace-nowrap">{r.name}</td>
                  <td className="p-4"><a href={`mailto:${r.email}`} className="text-[#0A2A63] hover:underline">{r.email}</a></td>
                  <td className="p-4 text-slate-600 whitespace-nowrap">{r.company || '—'}</td>
                  {tab === 'quotes' && <td className="p-4 text-slate-600">{productName(r.product_id)}</td>}
                  <td className="p-4 text-slate-600 max-w-md">{r.message || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminContacts;
